import { useEffect, useState } from "react";
import { useCanva } from "../context/CanvaContext.jsx";
import CanvaCard from "./CanvaCard.jsx";
import API from "../config/api.js";

// ─────────────────────────────────────────────────────────────────────────────
// My Media item card
// ─────────────────────────────────────────────────────────────────────────────
function MediaItem({ item }) {
  const isImage = item.type === "image";
  const id      = isImage ? item.data.idimage : item.data.idreel;
  const thumb   = isImage
    ? `${API.baseUrl}/${item.data.image}`
    : item.data.image
      ? `${API.baseUrl}/${item.data.image}`
      : "/assets/img/260x462.png";

  const handleClick = () => {
    const url = isImage
      ? `upload/image/${id}/image/original.jpg`
      : item.data.filewithmusic || item.data.file || `upload/reel/${id}/file/file.mp4`;
    window._draft_prompt_item_add?.(item.type, id, url);
  };

  return (
    <div className="col-6 col-md-3">
      <div
        className="position-relative rounded-3 overflow-hidden"
        style={{ aspectRatio: item.type === "reel" ? "9/16" : "1/1", cursor: "pointer", background: "#f2f2f2" }}
        onClick={handleClick}
      >
        <img
          src={thumb}
          alt=""
          className="w-100 h-100"
          style={{ objectFit: "cover" }}
          onError={(e) => { e.target.src = "/assets/img/260x462.png"; }}
          loading="lazy"
        />
        {item.type === "reel" && (
          <div className="position-absolute top-50 start-50 translate-middle">
            <img src="/assets/img/icon/play.png" width="40" alt="reel" />
          </div>
        )}
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center draft-media-hover">
          <i className="fi fi-rr-plus text-white fs-3" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// My Media tab
// ─────────────────────────────────────────────────────────────────────────────
function MyMediaTab() {
  const [media, setMedia]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  useEffect(() => {
    fetch(`${API.drafts}?listMyMedia`, { method: "POST", credentials: "include" })
      .then((r) => r.json())
      .then((data) => setMedia(Array.isArray(data.media) ? data.media : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>;
  if (error)   return <p className="text-danger text-center py-3">Error loading media.</p>;
  if (!media.length) return <p className="text-muted text-center py-3">No media found.</p>;

  return (
    <div className="row g-2">
      {media.map((item) => {
        const id = item.type === "image" ? item.data.idimage : item.data.idreel;
        return <MediaItem key={`${item.type}-${id}`} item={item} />;
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Canva tab
// ─────────────────────────────────────────────────────────────────────────────
function CanvaTab() {
  const { connected, designs, loading, hasMore, loadMore, fetchDesigns } = useCanva();

  useEffect(() => {
    if (connected && designs.length === 0) fetchDesigns("", true);
  }, [connected]); // eslint-disable-line

  if (!connected) {
    return (
      <div className="p-3 text-center text-muted">
        <p>Connect your Canva account to import designs.</p>
        <a href={`${API.baseUrl}/canva.php?getAuthorization=true`} className="btn btn-outline-primary btn-sm">
          <img src="/assets/img/canva-icon.webp" width="20" className="rounded-circle me-2" alt="Canva" />
          Connect With Canva
        </a>
      </div>
    );
  }

  return (
    <div>
      <div className="row g-2">
        {designs.map((design) => (
          <div key={design.id} className="col-6 col-md-4">
            <CanvaCard design={design} draftPrompt={true} />
          </div>
        ))}
      </div>
      {loading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>}
      {hasMore && !loading && (
        <div className="text-center mt-3">
          <button className="btn btn-outline-primary btn-sm" onClick={loadMore}>Load More</button>
        </div>
      )}
      {!loading && !designs.length && <p className="text-muted text-center py-3">No designs found.</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
export default function DraftPromptMyMedia({ onClose }) {
  const [activeTab, setActiveTab] = useState("my-media");

  return (
    <>
      <style>{`
        .draft-media-hover { background: rgba(0,0,0,0); transition: background 0.15s; }
        .draft-media-hover i { opacity: 0; transition: opacity 0.15s; }
        .draft-media-hover:hover { background: rgba(0,0,0,0.3) !important; }
        .draft-media-hover:hover i { opacity: 1 !important; }
      `}</style>

      <div
        className="position-absolute top-0 start-0 w-100 h-100 bg-white d-flex flex-column"
        style={{ zIndex: 50 }}
      >
        {/* Tabs header */}
        <ul className="nav nav-tabs mb-3 border-bottom px-3 pt-3" role="tablist">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "my-media" ? "active" : ""}`}
              onClick={() => setActiveTab("my-media")}
            >
              <i className="fi fi-rr-picture" />
              <span className="d-none d-md-inline-block ms-2">My Media</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "canva" ? "active" : ""}`}
              onClick={() => setActiveTab("canva")}
            >
              <img
                src="/assets/img/canva.svg"
                height="24"
                alt="Canva"
                style={{ filter: activeTab === "canva" ? "none" : "grayscale(1)" }}
              />
            </button>
          </li>
          <li className="ms-auto">
            <button className="btn btn-secondary" type="button" onClick={onClose}>
              Close My Media
            </button>
          </li>
        </ul>

        {/* Scrollable content */}
        <div className="flex-grow-1 overflow-y-auto overflow-x-hidden px-3 pb-3" style={{ minHeight: 0 }}>
          {activeTab === "my-media" && <MyMediaTab />}
          {activeTab === "canva"    && <CanvaTab />}
        </div>
      </div>
    </>
  );
}